import React, { useState, useEffect } from 'react';
import StaffDetail from './StaffDetail';
import { createPortal } from 'react-dom';
import { axiosInstance } from '../config';
import { toast } from 'react-hot-toast';

const StaffRow = ({ staff, onRefresh, onArchived, userEnvironment, onStaffUpdated }) => {
  const [showDetail, setShowDetail] = useState(false);
  const [commonEntitlementExpiry, setCommonEntitlementExpiry] = useState(null);
  const business = JSON.parse(localStorage.getItem('business') || '{}');
  const emailFrequency = business.email_frequency || 'Daily';
  const [showNotification, setShowNotification] = useState(false);
  const [notificationType, setNotificationType] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isCheckingLicense, setIsCheckingLicense] = useState(false);
  const canRunManualChecks = localStorage.getItem('businessCanRunManualChecks') === 'true' || business.can_run_manual_checks || false;

  console.log('Can run manual checks:', canRunManualChecks, typeof canRunManualChecks, localStorage.getItem('businessCanRunManualChecks'));

  useEffect(() => {
    const fetchCommonEntitlementExpiry = async () => {
      try {
        const response = await axiosInstance.get(`/api/staff/details/${staff.id}`);
        const licenseChecks = response.data.licenseChecks;
        
        // Get the most recent license check
        if (licenseChecks && licenseChecks.length > 0) {
          const latestCheck = licenseChecks.reduce((latest, check) => {
            return !latest || new Date(check.check_date) > new Date(latest.check_date) 
              ? check 
              : latest;
          }, null);

          if (latestCheck?.common_entitlement_expiry) {
            setCommonEntitlementExpiry(latestCheck.common_entitlement_expiry);
          }
        }
      } catch (error) {
        console.error('Error fetching common entitlement expiry:', error);
      }
    };

    // Add a small delay to prevent overwhelming the server
    const timer = setTimeout(() => {
      fetchCommonEntitlementExpiry();
    }, 500);

    return () => clearTimeout(timer);
  }, [staff.id]);

  // Debug log to see full staff object
  console.log('StaffRow data =>', {
    ...staff,
    vehicle_categories: staff.vehicle_categories
  });

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB');
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'N/A';
    }
  };

  const calculateNextCheckDate = (lastChecked, frequency) => {
    if (!lastChecked) return 'Not scheduled';
    
    // Get business data from localStorage
    const business = JSON.parse(localStorage.getItem('business') || '{}');
    const createdAt = business.created_at || business.created_date;
    
    if (!createdAt) {
      console.warn('No business creation date found');
      return 'Not scheduled';
    }

    const lastCheckDate = new Date(lastChecked);
    const now = new Date();
    let nextCheck = new Date(lastCheckDate); // Start from last check instead of creation date
    
    // Debug logs
    console.log('Last check date:', lastCheckDate);
    console.log('Current date:', now);
    console.log('Email frequency:', frequency);

    // Calculate next check based on frequency
    switch (frequency?.toLowerCase()) {
      case 'daily':
        nextCheck.setDate(nextCheck.getDate() + 1);
        break;
        
      case 'weekly':
        nextCheck.setDate(nextCheck.getDate() + 7);
        break;
        
      case 'monthly':
        nextCheck.setMonth(nextCheck.getMonth() + 1);
        break;
        
      case 'quarterly':
        nextCheck.setMonth(nextCheck.getMonth() + 3);
        break;
        
      case 'biannual':
      case 'semi-annual':
        nextCheck.setMonth(nextCheck.getMonth() + 6);
        break;
        
      case 'yearly':
      case 'annual':
        nextCheck.setFullYear(nextCheck.getFullYear() + 1);
        break;
        
      default:
        return 'Not scheduled';
    }

    // If the calculated next check is in the past, keep adding the interval until we get a future date
    while (nextCheck <= now) {
      switch (frequency?.toLowerCase()) {
        case 'daily':
          nextCheck.setDate(nextCheck.getDate() + 1);
          break;
        case 'weekly':
          nextCheck.setDate(nextCheck.getDate() + 7);
          break;
        case 'monthly':
          nextCheck.setMonth(nextCheck.getMonth() + 1);
          break;
        case 'quarterly':
          nextCheck.setMonth(nextCheck.getMonth() + 3);
          break;
        case 'biannual':
        case 'semi-annual':
          nextCheck.setMonth(nextCheck.getMonth() + 6);
          break;
        case 'yearly':
        case 'annual':
          nextCheck.setFullYear(nextCheck.getFullYear() + 1);
          break;
        default:
          // If frequency is not recognized, break the while loop
          nextCheck = new Date(now.getTime() + 86400000); // Add one day to break the loop
          console.warn(`Unrecognized frequency: ${frequency}`);
          break;
      }
    }

    console.log('Calculated next check date:', nextCheck);
    return formatDate(nextCheck);
  };

  const handleArchive = async () => {
    if (window.confirm('Are you sure you want to archive this staff member?')) {
      try {
        await axiosInstance.delete(`/api/staff/${staff.id}`);
        
        // Show success notification
        setNotificationType('success');
        setNotificationMessage(`${staff.first_name} ${staff.last_name} has been archived successfully`);
        setShowNotification(true);
        
        // Hide notification after 3 seconds
        setTimeout(() => {
          setShowNotification(false);
          // Call onArchived callback after notification is hidden
          if (onArchived) {
            onArchived(staff.id);
          }
        }, 3000);
        
      } catch (error) {
        console.error('Error archiving staff:', error);
        
        // Show error notification
        setNotificationType('error');
        setNotificationMessage('Failed to archive staff member');
        setShowNotification(true);
        
        // Hide error notification after 3 seconds
        setTimeout(() => {
          setShowNotification(false);
        }, 3000);
      }
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    
    // If birthday hasn't occurred yet this year, subtract one year
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const handleManualCheck = async () => {
    setIsCheckingLicense(true);
    try {
      const response = await axiosInstance.post(`/api/staff/check-license/${staff.id}`);
      console.log('Manual check response:', response.data);

      if (response.data.staff) {
        // Update the staff data with the latest check results
        const updatedStaff = {
          ...staff,
          ...response.data.staff,
          status: response.data.dvlaResponse.status,
          points_total: response.data.dvlaResponse.points_total || 0,
          last_checked: new Date().toISOString(),
          license_valid_from: response.data.dvlaResponse.license_valid_from,
          license_valid_to: response.data.dvlaResponse.expiry_date,
          common_entitlement_expiry: response.data.staff.common_entitlement_expiry
        };

        // Call onStaffUpdated if available, otherwise fallback to onRefresh
        if (onStaffUpdated) {
          onStaffUpdated(updatedStaff);
        } else if (onRefresh) {
          onRefresh();
        }

        toast.success(`License check completed for ${staff.first_name} ${staff.last_name}`);
      }

    } catch (error) {
      console.error('Error performing manual check:', error);
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message;
      toast.error(`Error checking license: ${errorMessage}`);
    } finally {
      setIsCheckingLicense(false);
    }
  };

  return (
    <>
      <tr>
        <td data-label="Name" className="name-cell">
          {staff.first_name} {staff.last_name}
        </td>
        <td data-label="License Number">{staff.driving_license_number}</td>
        <td data-label="Age" className="age-cell">{calculateAge(staff.date_of_birth)}</td>
        <td data-label="Status">
          <span className={`status ${(staff.status || '').toLowerCase()}`}>
            {staff.status || 'Unknown'}
          </span>
        </td>
        <td data-label="Points" className="points-cell">
          <span className={`points ${
            staff.points_total >= 6 ? 'high' : 
            staff.points_total >= 3 ? 'medium' : 
            'low'
          }`}>
            {staff.points_total || 0}
          </span>
        </td>
        <td data-label="Last Check Date">{formatDate(staff.last_checked)}</td>
        <td data-label="Next Check Date">
          {calculateNextCheckDate(staff.last_checked, emailFrequency)}
        </td>
        <td data-label="Card Expiry">{formatDate(staff.license_valid_to)}</td>
        <td data-label="License Valid Until">
          {commonEntitlementExpiry ? formatDate(commonEntitlementExpiry) : "Check Detail"}
        </td>
        <td data-label="Actions" className="actions-cell">
          <div className="action-buttons-container">
            <div className="primary-actions">
              <button onClick={() => setShowDetail(true)} className="view-details-btn">
                View
              </button>
              <button 
                onClick={handleArchive}
                className="archive-btn"
                title="Archive staff member"
              >
                Archive
              </button>
            </div>
            
            {canRunManualChecks && (
              <button 
                onClick={handleManualCheck}
                className="check-license-btn"
                disabled={isCheckingLicense}
                title="Perform manual DVLA check"
              >
                {isCheckingLicense ? 'Checking...' : 'Check License'}
              </button>
            )}
          </div>
        </td>
      </tr>
      
      {showNotification && createPortal(
        <div className={`notification ${notificationType}`} style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '15px 25px',
          borderRadius: '8px',
          zIndex: 1000,
          minWidth: '300px',
          textAlign: 'center',
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
          animation: 'slideInDown 0.3s ease',
          background: notificationType === 'success' ? '#d4edda' : '#f8d7da',
          color: notificationType === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${notificationType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          <div className="notification-content" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {notificationType === 'success' && <span className="icon">✓</span>}
            {notificationType === 'error' && <span className="icon">⚠</span>}
            {notificationMessage}
          </div>
        </div>,
        document.body
      )}
      
      {showDetail && createPortal(
        <StaffDetail
          staffId={staff.id}
          token={localStorage.getItem('token')}
          environment={userEnvironment}
          onClose={() => {
            setShowDetail(false);
            if (onRefresh) {
              onRefresh();
            }
          }} 
        />,
        document.body
      )}
    </>
  );
};

export default StaffRow; 